import { DeviceIcon } from "@/components/icons/DeviceIcon.tsx";
import { GmailIcon } from "@/components/icons/GmailIcon.tsx";
import { GoogleCalendarIcon } from "@/components/icons/GoogleCalendarIcon.tsx";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon.tsx";
import { GoogleIcon } from "@/components/icons/GoogleIcon.tsx";
import { GranolaIcon } from "@/components/icons/GranolaIcon.tsx";
import { sharedConnectorMarks } from "@/components/icons/sharedConnectorMarks.tsx";
import { connectorMarkFor } from "@/shared/connectors/connectorMark.ts";
import { providerDisplayName } from "@/shared/connectors/providers.ts";
import { MicrosoftIcon } from "@/components/icons/MicrosoftIcon.tsx";
import { OutlookIcon } from "@/components/icons/OutlookIcon.tsx";
import { SlackIcon } from "@/components/icons/SlackIcon.tsx";
import { TeamsIcon } from "@/components/icons/TeamsIcon.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { ZoomIcon } from "@/components/icons/ZoomIcon.tsx";
import { cn } from "@/lib/utils.ts";
import type { ConnectorIconVariant } from "@/types/artifacts-shared.ts";
import {
  DEVICE_CALENDAR_PROVIDER,
  DEVICE_CONTACTS_PROVIDER,
  LEGACY_DEVICE_PROVIDER,
} from "@/types/deviceConnectors.ts";
import { Mail, MessageSquare } from "lucide-react";
import type { ReactNode } from "react";

type ProviderIcon = (props: { className?: string }) => ReactNode;

/** An IMAP mailbox has no vendor mark, only the fact of being mail. */
const MailIcon: ProviderIcon = ({ className }) => (
  <Mail aria-label="Email" className={cn("text-muted-foreground", className)} />
);

/**
 * Every mark the shared asset directory holds, named for a screen reader as
 * the hand-written marks name themselves: the files are untitled by design,
 * and in a picker the mark is often the only thing that says which service
 * a row is.
 */
const sharedBrandProviderIcons: Record<string, ProviderIcon> = Object.fromEntries(
  Object.entries(sharedConnectorMarks).map(([slug, Mark]) => [
    slug,
    ({ className }: { className?: string }) => (
      <Mark role="img" aria-label={providerDisplayName(slug) ?? slug} className={className} />
    ),
  ]),
);

/**
 * The icon key for the Text Messages connector. It has no connector row or
 * provider behind it (it is the account's verified phone), so the key is ours.
 */
export const TEXT_MESSAGES_ICON_KEY = "textMessages";

/** Texts have no vendor mark either; the connector is the account's own number. */
const TextMessagesIcon: ProviderIcon = ({ className }) => (
  <MessageSquare aria-label="Text Messages" className={cn("text-muted-foreground", className)} />
);

/** Vendor brand marks, used when nothing more specific fits the connector. */
const brandProviderIcons: Record<string, ProviderIcon> = {
  // Spread first: the hand-written marks below carry product-specific
  // viewBoxes and labels and must win.
  ...sharedBrandProviderIcons,
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
  zoom: ZoomIcon,
  slack: SlackIcon,
  teams: TeamsIcon,
  gdrive: GoogleDriveIcon,
  whatsapp: WhatsAppIcon,
  // A pulled meeting note has no connector row — its whole provenance is the
  // service slug — so it reaches here as a brand rather than as mail or a
  // calendar.
  granola: GranolaIcon,
  imap: MailIcon,
  [TEXT_MESSAGES_ICON_KEY]: TextMessagesIcon,
  // The account's own calendar and contacts come from no third-party service.
  // Registered here, as native registers them, so a device-sourced row wears
  // the phone rather than nothing on this side.
  [DEVICE_CALENDAR_PROVIDER]: DeviceIcon,
  [DEVICE_CONTACTS_PROVIDER]: DeviceIcon,
  [LEGACY_DEVICE_PROVIDER]: DeviceIcon,
};

// Non-login Google/Microsoft connectors are calendar/contacts connectors, so they use
// calendar-specific icons. Zoom has no calendar variant — it falls back to its brand icon.
const calendarProviderIcons: Record<string, ProviderIcon> = {
  google: GoogleCalendarIcon,
  microsoft: OutlookIcon,
};

// Email inbox connectors represent a mailbox, so they use the mail-product icon.
const mailProviderIcons: Record<string, ProviderIcon> = {
  google: GmailIcon,
  microsoft: OutlookIcon,
  imap: MailIcon,
};

export function getConnectorIcon(
  provider: string,
  isLoginConnector: boolean,
  isEmailInbox: boolean,
  /** The mark's size, for callers drawing it somewhere other than a connector row. */
  className = "w-5 h-5",
) {
  // Login connectors use the brand icon, email-inbox connectors the mail icon,
  // and other non-login connectors (calendar/contacts sources) the calendar icon.
  const Icon =
    (!isLoginConnector && isEmailInbox && mailProviderIcons[provider]) ||
    (!isLoginConnector && calendarProviderIcons[provider]) ||
    brandProviderIcons[provider];
  if (Icon) {
    return <Icon className={className} />;
  }
  return null;
}

/**
 * Same icon choice as `getConnectorIcon`, addressed by variant rather than by
 * connector flags. A connect card knows which product it is offering to
 * connect but has no connector row yet to read `isLogin` / `isEmailInbox` off,
 * so it names the variant directly. Returns null when the provider has no
 * brand mark — callers fall back to `ConnectorLogo`'s favicon.
 */
export function getConnectorIconForVariant(
  provider: string,
  variant: ConnectorIconVariant,
  className?: string,
) {
  return getConnectorIcon(provider, variant === "brand", variant === "mail", className);
}

/**
 * The mark for anything that names a service, in two tiers: the provider's
 * product mark where we ship one, then the shared library by catalog slug or
 * homepage — which is how the admin-registered long tail, with no provider,
 * still draws Notion's or Airtable's mark rather than a favicon. Native's
 * `ConnectorIcon` resolves the same two tiers in the same order.
 *
 * A library mark is labelled with the service's own name: the file names are
 * slugs, and "Granola" reads better to a screen reader than "granola".
 *
 * Null when neither tier has a mark; callers fall back to `ConnectorLogo`'s
 * favicon.
 */
export function getServiceIcon(
  service: {
    name: string;
    provider?: string | null;
    iconVariant?: ConnectorIconVariant;
    slug?: string;
    websiteUrl?: string;
  },
  className = "w-5 h-5",
) {
  const productIcon = service.provider
    ? getConnectorIconForVariant(service.provider, service.iconVariant ?? "brand", className)
    : null;
  if (productIcon) return productIcon;
  const Mark = connectorMarkFor(sharedConnectorMarks, service);
  return Mark ? <Mark role="img" aria-label={service.name} className={className} /> : null;
}

// Moved to shared/client so native's connector rows title identically; the
// name maps live beside PROVIDER_DISPLAY_NAMES there and parallel the icon
// maps above so a row's title always matches its icon.
export { getConnectorTitle } from "@/shared/connectors/providers.ts";

/**
 * Icon identifying the mailbox a set of contacts came from.
 *
 * Prefers the mail-product mark (Gmail, Outlook) over the vendor brand: contacts
 * are synced per mailbox, and the plain Google mark reads the same for a Gmail
 * account as for a Google Calendar connector. Falls back to the brand mark, then
 * to nothing, so an unrecognized provider degrades to a text-only pill rather
 * than a broken image.
 */
export const ConnectorMailIcon = ({
  provider,
  className = "size-4",
}: {
  provider: string;
  className?: string;
}) => {
  const Icon = mailProviderIcons[provider] ?? brandProviderIcons[provider];
  return Icon ? <Icon className={className} /> : null;
};
